/**
 * Parses INSERT INTO ... VALUES statements to provide inlay hint positions.
 * Used by the Monaco inlay hints provider to show column names above/before VALUES.
 */

export type ValueHint = {
  columnName: string | null;
  /** Offset from start of the SQL string where the value begins */
  valueOffset: number;
  /** Length of the value text */
  valueLength: number;
};

export type InsertHintInfo = {
  tableName: string;
  /** Column names from the explicit column list, or null if no column list */
  columnNames: string[] | null;
  /** Each tuple is an array of value hints (one per value in the parenthesized list) */
  tuples: ValueHint[][];
};

export type InsertPrefix = {
  tableName: string;
  columnNames: string[] | null;
  /** Offset in the source text immediately after the VALUES keyword. */
  valuesOffset: number;
};

/**
 * Strips a quoted identifier (double-quotes, backticks, brackets) to its inner name.
 */
function stripQuotes(ident: string): string {
  const trimmed = ident.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) return trimmed.slice(1, -1);
  if (trimmed.startsWith('`') && trimmed.endsWith('`')) return trimmed.slice(1, -1);
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) return trimmed.slice(1, -1);
  return trimmed;
}

/**
 * Finds the matching closing parenthesis, respecting nested parens and string literals.
 * Returns the index of the closing paren, or -1 if not found.
 */
function findClosingParen(sql: string, openIndex: number): number {
  let depth = 1;
  let i = openIndex + 1;
  while (i < sql.length && depth > 0) {
    const ch = sql[i];
    if (ch === "'") {
      i++;
      while (i < sql.length) {
        if (sql[i] === "'" && sql[i + 1] === "'") {
          i += 2; // escaped quote
        } else if (sql[i] === "'") {
          break;
        } else {
          i++;
        }
      }
    } else if (ch === '-' && sql[i + 1] === '-') {
      // line comment
      while (i < sql.length && sql[i] !== '\n') i++;
    } else if (ch === '/' && sql[i + 1] === '*') {
      // block comment
      i += 2;
      while (i < sql.length - 1 && !(sql[i] === '*' && sql[i + 1] === '/')) i++;
      i++; // skip the '/'
    } else if (ch === '(') {
      depth++;
    } else if (ch === ')') {
      depth--;
      if (depth === 0) return i;
    }
    i++;
  }
  return -1;
}

/**
 * Splits a parenthesized list into individual value strings with their offsets,
 * respecting string literals, nested parens, and comments.
 */
function splitValues(sql: string, start: number, end: number): { offset: number; length: number }[] {
  const values: { offset: number; length: number }[] = [];
  let i = start;
  let valueStart = start;

  // Skip leading whitespace for the first value
  while (i < end && /\s/.test(sql[i])) i++;
  valueStart = i;

  // NOTE: the literal/comment/nested-paren grammar walked below mirrors
  // `findActiveValueIndex` — keep the two scanners in sync if new syntax is added.
  while (i < end) {
    const ch = sql[i];
    if (ch === "'") {
      i++;
      while (i < end) {
        if (sql[i] === "'" && sql[i + 1] === "'") {
          i += 2;
        } else if (sql[i] === "'") {
          break;
        } else {
          i++;
        }
      }
      i++;
    } else if (ch === '(') {
      const close = findClosingParen(sql, i);
      i = close === -1 ? end : close + 1;
    } else if (ch === '-' && sql[i + 1] === '-') {
      while (i < end && sql[i] !== '\n') i++;
    } else if (ch === '/' && sql[i + 1] === '*') {
      i += 2;
      while (i < end - 1 && !(sql[i] === '*' && sql[i + 1] === '/')) i++;
      i += 2;
    } else if (ch === ',') {
      // Found a separator — capture the value before it
      const valText = sql.slice(valueStart, i);
      const trimStart = valText.length - valText.trimStart().length;
      values.push({
        offset: valueStart + trimStart,
        length: valText.trim().length,
      });
      i++;
      // Skip whitespace after comma
      while (i < end && /\s/.test(sql[i])) i++;
      valueStart = i;
    } else {
      i++;
    }
  }

  // Capture the last value
  if (valueStart < end) {
    const valText = sql.slice(valueStart, end);
    const trimStart = valText.length - valText.trimStart().length;
    if (valText.trim().length > 0) {
      values.push({
        offset: valueStart + trimStart,
        length: valText.trim().length,
      });
    }
  }

  return values;
}

/**
 * Parses the prefix of an INSERT INTO ... VALUES statement: the INSERT keyword,
 * the table name (optionally schema-qualified and quoted), an optional explicit
 * column list, and the VALUES keyword. Returns enough information for both
 * `parseInsertForHints` (which continues on to parse tuples) and
 * `computeInsertSignatureHelp` (which walks forward from `valuesOffset` to find
 * the tuple containing the cursor).
 *
 * Returns null if the text is not a parseable INSERT...VALUES prefix.
 */
export function parseInsertPrefix(sql: string): InsertPrefix | null {
  if (!sql || !sql.trim()) return null;

  const insertRegex = /\bINSERT\s+INTO\s+/i;
  const insertMatch = insertRegex.exec(sql);
  if (!insertMatch) return null;

  let pos = insertMatch.index + insertMatch[0].length;

  while (pos < sql.length && /\s/.test(sql[pos])) pos++;

  const identPartRegex = /^(?:"(?:[^"]|"")*"|\[(?:[^\]])*\]|`(?:[^`])*`|[A-Za-z_]\w*)/;

  let tableName = '';
  const firstPart = identPartRegex.exec(sql.slice(pos));
  if (!firstPart) return null;
  tableName = firstPart[0];
  pos += firstPart[0].length;

  if (sql[pos] === '.') {
    tableName += '.';
    pos++;
    const secondPart = identPartRegex.exec(sql.slice(pos));
    if (secondPart) {
      tableName += secondPart[0];
      pos += secondPart[0].length;
    }
  }

  if (!tableName) return null;

  pos = skipWhitespaceAndComments(sql, pos);

  let columnNames: string[] | null = null;
  if (sql[pos] === '(') {
    const closeParen = findClosingParen(sql, pos);
    if (closeParen === -1) return null;
    const columnListText = sql.slice(pos + 1, closeParen);
    columnNames = parseColumnList(columnListText);
    pos = closeParen + 1;
  }

  pos = skipWhitespaceAndComments(sql, pos);

  const valuesRegex = /^VALUES\b/i;
  const valuesMatch = valuesRegex.exec(sql.slice(pos));
  if (!valuesMatch) return null;
  pos += valuesMatch[0].length;

  return { tableName, columnNames, valuesOffset: pos };
}

/**
 * Walks forward from `searchStart` looking for the `(...)` tuple whose range
 * (from open paren, inclusive, to matching close paren or end-of-string for
 * unclosed tuples, inclusive) contains `cursorOffset`. Skips whitespace,
 * comments, and inter-tuple commas. Returns the open paren offset, or -1 if
 * none found.
 *
 * Used by `computeInsertSignatureHelp` to locate the active tuple when there
 * are multiple `VALUES (...), (...), (...)` rows.
 */
export function findEnclosingTupleOpenParen(
  sql: string,
  searchStart: number,
  cursorOffset: number,
): number {
  let pos = searchStart;
  while (pos < sql.length) {
    pos = skipWhitespaceAndComments(sql, pos);
    if (pos >= sql.length) return -1;
    if (sql[pos] !== '(') return -1;

    const openParen = pos;
    const closeParen = findClosingParen(sql, openParen);
    const rangeEnd = closeParen === -1 ? sql.length : closeParen;

    if (cursorOffset >= openParen && cursorOffset <= rangeEnd) {
      return openParen;
    }

    if (cursorOffset < openParen) return -1;

    if (closeParen === -1) return -1;
    pos = closeParen + 1;
    pos = skipWhitespaceAndComments(sql, pos);
    if (sql[pos] === ',') {
      pos++;
    } else {
      return -1;
    }
  }
  return -1;
}

/**
 * Given the offset of a tuple's open paren (sql[openParen] must be `(`), counts
 * top-level commas between `openParen + 1` and `cursorOffset`, respecting
 * string literals, line/block comments, and nested parens. Returns the active
 * value index (0 = first value).
 *
 * The count uses a half-open `[openParen + 1, cursorOffset)` range, matching
 * Monaco's offset semantics where `getOffsetAt(position)` returns the offset of
 * the character the cursor sits before. A cursor immediately after a typed
 * comma therefore counts that comma; a cursor at the offset of a comma
 * character does not.
 *
 * Stops at the tuple's matching close paren if reached before `cursorOffset`.
 * For unclosed tuples, counts through to `cursorOffset` or end-of-string,
 * whichever comes first.
 */
export function findActiveValueIndex(
  sql: string,
  openParen: number,
  cursorOffset: number,
): number {
  if (cursorOffset <= openParen) return 0;

  const end = Math.min(sql.length, cursorOffset);
  let i = openParen + 1;
  let commaCount = 0;

  // NOTE: the literal/comment/nested-paren grammar walked below mirrors
  // `splitValues` — keep the two scanners in sync if new syntax is added.
  while (i < end) {
    const ch = sql[i];
    if (ch === "'") {
      i++;
      while (i < end) {
        if (sql[i] === "'" && sql[i + 1] === "'") {
          i += 2;
        } else if (sql[i] === "'") {
          i++;
          break;
        } else {
          i++;
        }
      }
    } else if (ch === '(') {
      const close = findClosingParen(sql, i);
      if (close === -1 || close >= end) {
        // Unclosed nested paren before cursor — cursor is inside a nested call,
        // no further top-level commas possible before cursor.
        return commaCount;
      }
      i = close + 1;
    } else if (ch === '-' && sql[i + 1] === '-') {
      while (i < end && sql[i] !== '\n') i++;
    } else if (ch === '/' && sql[i + 1] === '*') {
      i += 2;
      while (i < end - 1 && !(sql[i] === '*' && sql[i + 1] === '/')) i++;
      i += 2;
    } else if (ch === ')') {
      // Tuple's own closing paren — stop counting.
      break;
    } else if (ch === ',') {
      commaCount++;
      i++;
    } else {
      i++;
    }
  }

  return commaCount;
}

/**
 * Parses an INSERT INTO ... VALUES (...) statement and returns information
 * needed to generate inlay hints for column names above each value.
 *
 * Returns null if the text is not an INSERT...VALUES statement with at least
 * one tuple.
 */
export function parseInsertForHints(sql: string): InsertHintInfo | null {
  const prefix = parseInsertPrefix(sql);
  if (!prefix) return null;

  const { tableName, columnNames } = prefix;
  let pos = prefix.valuesOffset;

  const tuples: ValueHint[][] = [];

  while (pos < sql.length) {
    pos = skipWhitespaceAndComments(sql, pos);

    if (sql[pos] !== '(') break;

    const closeParen = findClosingParen(sql, pos);
    if (closeParen === -1) break;

    const valuePositions = splitValues(sql, pos + 1, closeParen);
    const hints: ValueHint[] = valuePositions.map((vp, idx) => ({
      columnName: columnNames ? (columnNames[idx] ?? null) : null,
      valueOffset: vp.offset,
      valueLength: vp.length,
    }));
    tuples.push(hints);

    pos = closeParen + 1;
    pos = skipWhitespaceAndComments(sql, pos);

    if (sql[pos] === ',') {
      pos++;
    } else {
      break;
    }
  }

  if (tuples.length === 0) return null;

  return { tableName, columnNames, tuples };
}

/**
 * Extract the plain table name (without schema/quotes) for schema cache lookup.
 */
export function extractPlainTableName(tableName: string): string {
  // Handle schema.table — take the last part
  const parts = tableName.split('.');
  const lastPart = parts[parts.length - 1];
  return stripQuotes(lastPart);
}

/**
 * Split a dotted identifier into its segments while respecting quoted parts.
 * A dot inside a quoted segment (e.g. `[Weird.Name]` or `"col.with.dots"`) is
 * treated as part of the identifier, not a separator.
 */
function splitDottedIdentifier(raw: string): string[] {
  const segments: string[] = [];
  let current = '';
  let i = 0;

  while (i < raw.length) {
    const ch = raw[i];
    if (ch === '"' || ch === '`' || ch === '[') {
      const closer = ch === '[' ? ']' : ch;
      current += ch;
      i++;
      while (i < raw.length) {
        current += raw[i];
        if (raw[i] === closer) {
          i++;
          break;
        }
        i++;
      }
    } else if (ch === '.') {
      segments.push(current);
      current = '';
      i++;
    } else {
      current += ch;
      i++;
    }
  }
  segments.push(current);
  return segments;
}

/**
 * Parse a dotted table identifier into schema + table parts.
 *
 * Handles:
 *   - Plain:                   `Product`
 *   - Schema-qualified:        `Production.Product`
 *   - Bracket-quoted (MSSQL):  `[Production].[Product]`
 *   - Double-quoted:           `"Production"."Product"`
 *   - Backtick-quoted (MySQL): `` `Production`.`Product` ``
 *   - Mixed quoting:           `Production.[Product With Spaces]`
 *   - Surrounding whitespace:  `  Production . Product  `
 *
 * Three-part names (`db.schema.table`) fall through to the two-part branch:
 * the last two segments are used as schema + table, the leading db segment is
 * dropped. This matches the fact that downstream `getTableMetadata` only takes
 * schema + table.
 *
 * For malformed input (empty parts after the split — e.g. `users.` or `.users`),
 * returns `{ table: raw }` as a safe fallback so downstream lookups fail cleanly
 * rather than passing an empty schema/table to the IPC.
 */
export function parseQualifiedTableName(raw: string): { schema?: string; table: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { table: '' };

  const parts = splitDottedIdentifier(trimmed).map((p) => p.trim());

  if (parts.length === 1) {
    return { table: stripQuotes(parts[0]) };
  }

  // Two or more parts: use the last two as schema.table. Drop any leading
  // db/server segments (out-of-scope for this slice).
  const tablePart = parts[parts.length - 1];
  const schemaPart = parts[parts.length - 2];

  // Safe fallback for empty parts (malformed input).
  if (!tablePart || !schemaPart) {
    return { table: raw };
  }

  return {
    schema: stripQuotes(schemaPart),
    table: stripQuotes(tablePart),
  };
}

function parseColumnList(text: string): string[] {
  const columns: string[] = [];
  let current = '';
  let inBlockComment = false;

  for (let i = 0; i < text.length; i++) {
    if (inBlockComment) {
      if (text[i] === '*' && text[i + 1] === '/') {
        inBlockComment = false;
        i++;
      }
      continue;
    }
    if (text[i] === '/' && text[i + 1] === '*') {
      inBlockComment = true;
      i++;
      continue;
    }
    if (text[i] === '-' && text[i + 1] === '-') {
      while (i < text.length && text[i] !== '\n') i++;
      continue;
    }
    if (text[i] === ',') {
      const col = current.trim();
      if (col) columns.push(stripQuotes(col));
      current = '';
    } else {
      current += text[i];
    }
  }
  const col = current.trim();
  if (col) columns.push(stripQuotes(col));

  return columns;
}

function skipWhitespaceAndComments(sql: string, pos: number): number {
  while (pos < sql.length) {
    if (/\s/.test(sql[pos])) {
      pos++;
    } else if (sql[pos] === '-' && sql[pos + 1] === '-') {
      while (pos < sql.length && sql[pos] !== '\n') pos++;
    } else if (sql[pos] === '/' && sql[pos + 1] === '*') {
      pos += 2;
      while (pos < sql.length - 1 && !(sql[pos] === '*' && sql[pos + 1] === '/')) pos++;
      pos += 2;
    } else {
      break;
    }
  }
  return pos;
}
